import { observer } from 'mobx-react';
import cn from 'classnames';
import { Popover, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { TooltipPlacement } from 'antd/lib/tooltip';

import { useStores } from '@/hooks/use-stores';
import { formatThousands } from '@/utils/util-functions';

import { Container, ValueContainer } from './styled';
import Scale from './Scale/Scale';
import KPISkeleton from './KPISkeleton/KPISkeleton';

interface ScaleProps {
  values: number[];
  colors: string[];
  fadedColors: string[];
  shouldShowEdgeOnly?: boolean;
}

interface KPIBoxProps {
  title: string;
  icon?: JSX.Element;
  value: number;
  unit: string | JSX.Element;
  scale: ScaleProps;
  isRelativeScale?: boolean;
  isVertical?: boolean;
  tooltip?: string | JSX.Element;
  tooltipPlacement?: TooltipPlacement;
  $specialStyleKPIBox?: boolean;
  collapsed?: boolean;
  tierInfo?: JSX.Element;
  tierLockIsVisible?: boolean;
  hideTooltip?: boolean;
  targetValue?: number;
}

const { useToken } = theme;

const KPIBox = observer(
  ({
    title,
    icon,
    value,
    unit,
    scale,
    isRelativeScale = true,
    isVertical = false,
    tooltip,
    tooltipPlacement,
    $specialStyleKPIBox,
    collapsed,
    tierInfo,
    tierLockIsVisible = false,
    hideTooltip,
    targetValue,
  }: KPIBoxProps) => {
    const { t } = useTranslation();
    const { stationStore } = useStores();
    const { token } = useToken();

    const trianglePositionValAtFixedScale = () => {
      let val = -1;
      let r0 = scale.values[0];
      let r1 = scale.values[1];
      let i = 0;
      scale.values.forEach((range, index) => {
        if (value >= range) {
          r0 = range;
          val = value - r0;
          i = index;
          r1 = index === scale.values.length - 1 ? scale.values[index] : scale.values[index + 1];
        }
      });
      return (100 / (scale.values.length - 1)) * i + (val / (r1 - r0)) * (100 / (scale.values.length - 1));
    };

    const getValueColour = (value): string => {
      let colour = scale.colors[0];
      if (scale) {
        scale.values.forEach((range, index) => {
          if (value >= range) {
            colour = scale.colors[index];
          }
        });
      }
      if (colour === undefined) {
        colour = scale.colors[scale.colors.length - 1];
      }
      return colour;
    };

    let trianglePosition: () => number;
    let targetPosition: number;
    let totalRange: number;
    if (scale) {
      totalRange = scale.values[scale.values.length - 1] - scale.values[0];
      trianglePosition = () => {
        let position = isRelativeScale
          ? ((value - scale.values[0]) * 100) / totalRange
          : trianglePositionValAtFixedScale();
        if (position < 0) {
          position = 0;
        } else if (position > 100) {
          position = 100;
        }
        return position;
      };

      if (targetValue !== undefined) {
        targetPosition = isRelativeScale
          ? ((targetValue - scale.values[0]) * 100) / totalRange
          : ((targetValue - scale.values[0]) * 100) / (scale.values[scale.values.length - 1] - scale.values[0]);
        targetPosition = Math.max(0, Math.min(100, targetPosition));
      }
    }

    const renderValue = value =>
      $specialStyleKPIBox ? (
        formatThousands(value)
      ) : (
        <>
          {value}
          {title === t('dissolvedOxygen') && `/${scale.values[scale.values.length - 1] || 0}`}
        </>
      );
    const isPopoverVisible = !tierLockIsVisible && !hideTooltip;
    return (
      <Container className={collapsed ? 'collapsed' : ''}>
        <div className="title-container">
          {icon && <span className="icon">{icon}</span>}
          <span className="title">{title}</span>
        </div>

        <KPISkeleton loading={stationStore.isLoading} style={{ marginTop: token.marginXS, height: '100%' }}>
          <ValueContainer
            style={{ borderColor: getValueColour(value) }}
            className={'value-container'}
            $vertical={isVertical}
            $specialStyleKPIBox={$specialStyleKPIBox}
            $location={trianglePosition && trianglePosition()}
            $valueLength={`${value ?? ''}`.length}
          >
            <div className="value-container-wrapper">
              <Popover
                placement={tooltipPlacement ?? 'bottom'}
                content={isPopoverVisible ? tooltip ?? t('Value may depend on multiple sensors.') : null}
              >
                <span className={cn('value', { bold: value })}>
                  {/* TO DO remove wastewater check */}
                  {value != null && !stationStore.isWasteWaterStation
                    ? isNaN(value)
                      ? 'N/A'
                      : renderValue(value)
                    : '_ _'}
                </span>
              </Popover>
              <span className="unit">
                {unit}
                {value !== null && value !== undefined && !Number.isNaN(value) && tierInfo}
              </span>
            </div>
            {collapsed && <div className="border-bottom" style={{ backgroundColor: getValueColour(value) }} />}
          </ValueContainer>

          {!collapsed && scale && (
            <Scale
              scale={scale}
              value={value}
              isRelativeScale={isRelativeScale}
              unit={unit}
              $specialStyleKPIBox={$specialStyleKPIBox}
              totalRange={totalRange}
              trianglePosition={trianglePosition()}
              targetValue={targetValue}
              targetPosition={targetPosition}
            />
          )}
        </KPISkeleton>
      </Container>
    );
  },
);

export default KPIBox;
